<template>
  <div class="geometry-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-geo">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
           </defs>

           <!-- 背景网格 -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="1.5">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <g :transform="`translate(400, 300) rotate(${angle}) scale(${zoom})`">
              <!-- 模式：三角形五心 -->
              <g v-if="mode === 'centers'">
                 <polygon :points="trianglePointsStr" fill="rgba(16, 185, 129, 0.08)" stroke="#10b981" stroke-width="4" filter="url(#glow-geo)" />
                 
                 <!-- 各中心点 -->
                 <circle :cx="orthocenter.x" :cy="orthocenter.y" r="8" fill="#ef4444" stroke="#fff" stroke-width="2" />
                 <text :x="orthocenter.x + 15" :y="orthocenter.y + 5" fill="#fca5a5" font-size="22" font-weight="900" style="text-shadow: 0 0 5px #000">垂心 (H)</text>

                 <circle :cx="incenter.x" :cy="incenter.y" r="8" fill="#06b6d4" stroke="#fff" stroke-width="2" />
                 <text :x="incenter.x + 15" :y="incenter.y + 30" fill="#93ceee" font-size="22" font-weight="900" style="text-shadow: 0 0 5px #000">内心 (I)</text>
                 <circle :cx="incenter.x" :cy="incenter.y" :r="inradius" fill="none" stroke="rgba(6, 182, 212, 0.4)" stroke-width="3" stroke-dasharray="8,6" />
              </g>

              <!-- 模式：蝴蝶模型 -->
              <g v-if="mode === 'butterfly'">
                 <!-- 梯形底座 -->
                 <polygon points="-150,-100 150,-100 200,100 -200,100" fill="none" stroke="#94a3b8" stroke-width="3" stroke-dasharray="6,6" />
                 <!-- 蝴蝶翅膀 -->
                 <polygon points="-150,-100 200,100 0,0" fill="rgba(59, 130, 246, 0.3)" stroke="#3b82f6" stroke-width="4" />
                 <polygon points="150,-100 -200,100 0,0" fill="rgba(245, 158, 11, 0.3)" stroke="#f59e0b" stroke-width="4" />
                 
                 <text x="-80" y="25" fill="#fff" font-size="28" text-anchor="middle" font-weight="900">S₁</text>
                 <text x="80" y="25" fill="#fff" font-size="28" text-anchor="middle" font-weight="900">S₂</text>
                 <text x="0" y="-130" fill="#f8fafc" font-size="20" text-anchor="middle" font-weight="bold">面积相等: S₁ = S₂</text>
              </g>

              <!-- 模式：燕尾模型 -->
              <g v-if="mode === 'swallowtail'">
                 <polygon points="0,-180 -140,120 140,120" fill="none" stroke="#475569" stroke-width="3" />
                 <line x1="0" y1="-180" x2="0" y2="120" stroke="#fca5a5" stroke-width="3" stroke-dasharray="6,4" />
                 <polygon points="0,-180 -140,120 0,40" fill="rgba(239, 68, 68, 0.25)" stroke="#ef4444" stroke-width="3" />
                 <polygon points="0,-180 140,120 0,40" fill="rgba(16, 185, 129, 0.25)" stroke="#10b981" stroke-width="3" />
                 <text x="-50" y="-20" fill="#fff" font-size="24" font-weight="900">S左</text>
                 <text x="50" y="-20" fill="#fff" font-size="24" font-weight="900">S右</text>
              </g>

              <!-- 标注说明 -->
              <text x="-380" y="-260" fill="rgba(255,255,255,0.4)" font-size="16" font-weight="bold">{{ modeTitle }} 演示视图</text>
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>对象类型</label>
              <span class="val">{{ modeTitle }}</span>
           </div>
           <div class="hud-item cyan">
              <label>几何属性</label>
              <span class="val">{{ propertyLabel }}</span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
           <div class="control-grid-mini">
              <div class="s-item">
                 <label>旋转角度 ({{ angle }}°)</label>
                 <el-slider v-model="angle" :min="0" :max="360" size="small"></el-slider>
              </div>
              <div class="s-item">
                 <label>缩放比例 ({{ zoom.toFixed(1) }}x)</label>
                 <el-slider v-model="zoom" :min="0.5" :max="2.0" :step="0.1" size="small"></el-slider>
              </div>
              <div class="s-item full">
                 <label>形变参数调节</label>
                 <el-slider v-model="offset" :min="-50" :max="50" size="small"></el-slider>
              </div>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <div class="discovery-content">
              <p><strong>几何本质：</strong>{{ modeDesc }}。{{ tipText }}</p>
           </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GeometryViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'centers',
      angle: 0,
      zoom: 1.2,
      offset: 0,
      A: { x: 0, y: -130 },
      B: { x: -150, y: 120 },
      C: { x: 150, y: 120 }
    }
  },
  mounted() {
    this.detectMode();
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    trianglePointsStr() {
       const ox = this.offset;
       return `0,${this.A.y} ${this.B.x + ox},${this.B.y} ${this.C.x + ox},${this.C.y}`;
    },
    topicIntro() {
       const maps = {
          centers: '三角形五心：探究垂直、平分与重心的平衡位置',
          butterfly: '蝴蝶模型：梯形与四边形中的对顶面积守恒规律',
          swallowtail: '燕尾模型：共底三角形在比例缩放下的面积关系',
          general: '平面几何：基础图形的性质、判定与逻辑推导'
       };
       return maps[this.mode] || '几何模型仿真';
    },
    modeTitle() {
       const maps = { centers: '三角形五心', butterfly: '蝴蝶模型', swallowtail: '燕尾模型' };
       return maps[this.mode] || '常规几何';
    },
    activeFormula() {
       if (this.mode === 'butterfly') return 'S₁ : S₂ = a : b';
       if (this.mode === 'swallowtail') return 'S左 : S右 = m : n';
       return 'S = 1/2 · b · h | 垂心 (H)';
    },
    propertyLabel() {
       if (this.mode === 'centers') return '垂心 / 内心 渲染';
       if (this.mode === 'butterfly') return '面积等值特性';
       return '线性比例映射';
    },
    modeDesc() {
       const maps = {
          centers: '垂心是三条高的交点，内心是角平分线的交点（内切圆心）',
          butterfly: '在梯形中，对角线分出的两个侧面三角形面积永远相等',
          swallowtail: '通过顶点向底边引线，形成的两个大三角形面积比等于底边分成的线段比'
       };
       return maps[this.mode] || '';
    },
    tipText() {
       return '拖动滑块，观察几何模型在变形过程中保持不变的性质。';
    },
    orthocenter() { return { x: 10 + this.offset/10, y: 30 }; },
    incenter() { return { x: 0, y: 20 }; },
    inradius() { return 55 + this.offset/4; }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/蝴蝶/.test(kp)) this.mode = 'butterfly';
      else if (/燕尾/.test(kp)) this.mode = 'swallowtail';
      else if (/心|性质|判定|平行/.test(kp)) this.mode = 'centers';
      else this.mode = 'centers';
    }
  }
}
</script>

<style scoped lang="scss">
.geometry-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 14px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f8fafc; color: #1e40af; border: 1px solid #cbd5e1; padding: 5px 16px; border-radius: 20px; font-weight: 900; font-family: "Courier New", monospace; font-size: 14px; } }
.simulation-stage { flex: 1; min-height: 380px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 15px; right: 15px; display: flex; flex-direction: column; gap: 10px; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 10px; border: 1px solid #334155; padding: 6px 14px; label { display: block; font-size: 11px; color: #94a3b8; font-weight: 900; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 900; } &.blue { border-left: 4px solid #3b82f6; } &.cyan { border-left: 4px solid #10b981; } } } }
.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 14px 18px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 12px; color: #475569; font-weight: 900; margin-bottom: 4px; display: block; } &.full { grid-column: span 2; } } }
.discovery-card-compact { background: #f8fafc; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; display: flex; align-items: flex-start; gap: 12px; i { color: #3b82f6; font-size: 1.2rem; margin-top: 2px; } .discovery-content { flex: 1; p { margin: 0; font-size: 13px; color: #334155; line-height: 1.6; strong { color: #0f172a; font-weight: 900; } } } }
</style>
